/**
 * Rostering Service (client) — Clever SSO + roster sync.
 *
 * Audit gap: Clever/ClassLink were type-strings only. This is the real client
 * surface for the "Log in with Clever" OAuth flow and the admin-triggered
 * roster sync. The heavy lifting lives in the `cleverLoginExchange` /
 * `syncCleverRoster` Cloud Functions (see functions/src/cleverIntegration.ts).
 *
 * Credential-gated: until REACT_APP_CLEVER_CLIENT_ID is set, isCleverEnabled()
 * returns false and the UI should hide the Clever entry points. The server
 * functions independently fail with `clever_not_configured` if called without
 * credentials. This client code is UNTESTED against a live Clever app.
 */

import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';
import { callFunction } from '../utils/functions';
import { createComponentLogger } from '../utils/logger';
import { getErrorMessage } from '../utils/errorHandler';
import { secureToken } from '../utils/secureId';

const logger = createComponentLogger('RosteringService');

const CLEVER_AUTHORIZE_URL = 'https://clever.com/oauth/authorize';

/** Redirect URI registered with the Clever OAuth app: <origin>/clever/callback */
function cleverRedirectUri(): string {
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return `${origin}/clever/callback`;
}

/**
 * True when a Clever OAuth client id is configured at build time. Gate all
 * Clever UI on this so nothing renders when the integration isn't set up.
 */
export function isCleverEnabled(): boolean {
  return !!process.env.REACT_APP_CLEVER_CLIENT_ID;
}

/**
 * Build the Clever OAuth authorize URL the "Log in with Clever" button sends
 * the user to. `district_id` is optional — Clever shows a district picker when
 * omitted, or you can pin it via REACT_APP_CLEVER_DISTRICT_ID.
 */
/**
 * S82: where the one-time OAuth `state` lives between the redirect out and the
 * redirect back. `sessionStorage`, not `localStorage`: it is scoped to this tab,
 * so a shared classroom device cannot carry one child's pending login into
 * another child's tab.
 */
const CLEVER_STATE_KEY = 'nsgs_clever_oauth_state';

export function getCleverLoginUrl(): string {
  const clientId = process.env.REACT_APP_CLEVER_CLIENT_ID || '';
  const districtId = process.env.REACT_APP_CLEVER_DISTRICT_ID || '';

  // S82: refuse to build a link at all when there is no client id. `|| ''` meant
  // this never failed: it produced a link to Clever's authorize endpoint with
  // `client_id=`, which Clever answers with its own error page — so an
  // unconfigured deployment showed a child a Clever-branded failure instead of
  // the app saying the district is not connected yet.
  if (!clientId) {
    throw new Error(
      'Clever sign-in is not configured for this deployment (no REACT_APP_CLEVER_CLIENT_ID).'
    );
  }

  // S82: OAuth 2.0 uses `state` to bind the redirect back to the browser that
  // started the flow. Neither half of this integration had one, so anyone holding
  // a valid Clever authorization code for THEIR OWN account could hand a child a
  // link to `<origin>/clever/callback?code=<attacker code>`; the page redeemed it
  // and silently signed the child into the attacker's account, where every game
  // the child then wrote could be read. Replaying a victim's code was equally
  // undetectable. `secureToken` throws rather than degrading to Math.random().
  const state = secureToken(32);
  try {
    window.sessionStorage.setItem(CLEVER_STATE_KEY, state);
  } catch (error) {
    // A browser that cannot store it cannot verify it either, and an
    // unverifiable OAuth flow is one we do not start.
    throw new Error(
      'Clever sign-in needs session storage, which this browser has blocked. ' +
        'Please enable it, or sign in with an email address instead.'
    );
  }

  const params = new URLSearchParams({
    response_type: 'code',
    redirect_uri: cleverRedirectUri(),
    client_id: clientId,
    state,
  });
  if (districtId) {
    params.set('district_id', districtId);
  }
  return `${CLEVER_AUTHORIZE_URL}?${params.toString()}`;
}

/**
 * S145: the server's own machine codes, translated for the child reading them.
 *
 * `cleverIntegration.ts` throws `failed-precondition: clever_not_configured`
 * when a district's credentials are missing, and `callFunction` has no branch
 * for failed-precondition — so it re-threw the raw message, this service
 * prefixed it, and `CleverCallback` rendered it straight into the visible error
 * box. A K-5 student tapping "Log in with Clever" against an unconfigured
 * deployment read "Failed to sign in with Clever: clever_not_configured": an
 * internal configuration state, phrased as machine text, with no next step.
 */
const CLEVER_ERROR_COPY: Record<string, string> = {
  clever_not_configured:
    'your school has not finished setting up Clever. Please tell your teacher.',
  clever_token_exchange_failed:
    'Clever did not accept the sign-in. Please try again, or ask your teacher for help.',
  clever_user_not_found: 'Clever does not have an account for you yet. Please ask your teacher.',
  clever_district_not_authorized:
    'your school is not set up to use this app with Clever yet. Please tell your teacher.',
};

const humanCleverError = (error: unknown): string => {
  const raw = getErrorMessage(error);
  for (const [code, copy] of Object.entries(CLEVER_ERROR_COPY)) {
    if (raw.includes(code)) return copy;
  }
  // Anything with no underscore-separated machine shape is already prose.
  return /^[a-z0-9]+(_[a-z0-9]+)+$/.test(raw.trim())
    ? 'something went wrong at your school\'s end. Please ask your teacher for help.'
    : raw;
};

/**
 * Verify and consume the `state` Clever handed back.
 *
 * Single-use: the stored value is removed whether or not it matched, so a code
 * cannot be replayed against the same pending login.
 */
export function consumeCleverState(returnedState: string | null): boolean {
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(CLEVER_STATE_KEY);
    window.sessionStorage.removeItem(CLEVER_STATE_KEY);
  } catch {
    return false;
  }
  if (!stored || !returnedState) return false;
  return stored === returnedState;
}

/**
 * Complete the Clever login: exchange the OAuth `code` for a Firebase custom
 * token via the server, then sign in. The redirectUri MUST match the one used
 * to start the flow (Clever validates it during the token exchange).
 */
export async function completeCleverLogin(code: string): Promise<void> {
  try {
    if (!code) {
      throw new Error('Missing Clever authorization code.');
    }
    const response = (await callFunction('cleverLoginExchange', {
      code,
      redirectUri: cleverRedirectUri(),
    })) as { token?: unknown } | null;

    // S82: the token was destructured and handed straight to the Auth SDK. A
    // response with no token passed `undefined`, and an empty string passed `''` —
    // neither is a sign-in, and neither produced a message anyone could act on.
    // Rule 8d: a server response is a claim, not a guarantee.
    const token = response?.token;
    if (typeof token !== 'string' || token === '') {
      throw new Error('Clever did not return a sign-in token.');
    }

    await signInWithCustomToken(auth.value, token);
    logger.info('Completed Clever login and signed in with custom token');
  } catch (error) {
    logger.error('Error completing Clever login:', error);
    throw new Error(`Failed to sign in with Clever: ${humanCleverError(error)}`);
  }
}

/**
 * Trigger a district roster sync (admin only — the server enforces the admin
 * custom claim). Returns the run summary persisted to rosterSyncJobs/{id}.
 */
/** What one roster sync did — persisted to `rosterSyncJobs/{id}` as well. */
export interface RosterSyncSummary {
  startedAt: string;
  finishedAt: string;
  sectionsProcessed: number;
  usersUpserted: number;
  /** People tied to one of our schools by this run (task 115). */
  usersStampedToSchool?: number;
  /** Clever schools nobody here is mapped to; their people were imported with no school. */
  unmappedCleverSchools?: string[];
  /** People already in a different school, left where they were. */
  schoolConflicts?: number;
  status: 'success' | 'error';
  error?: string;
}

export async function syncCleverRoster(): Promise<RosterSyncSummary> {
  try {
    const summary = (await callFunction('syncCleverRoster', {})) as RosterSyncSummary;
    // S145: the declared return type has `status: 'success' | 'error'`, and this
    // logged "Clever roster sync completed" for either while nothing inspected
    // it — so the first caller to write `await syncCleverRoster();
    // toast('Roster imported')` would announce a successful import of a run that
    // imported nothing. Standing rule 7: an operation that could not do its job
    // must FAIL.
    if (summary?.status === 'error') {
      throw new Error(summary.error || 'The roster sync did not finish.');
    }
    logger.info('Clever roster sync completed', summary);
    return summary;
  } catch (error) {
    logger.error('Error syncing Clever roster:', error);
    throw new Error(`Failed to sync Clever roster: ${getErrorMessage(error)}`);
  }
}

/**
 * Record which Clever school this school is (or clear it with ''). Platform operator only —
 * the server refuses anyone else, because the mapping decides which school a whole Clever
 * school's children are filed under on the next sync.
 */
export async function setSchoolCleverId(
  schoolId: string,
  cleverSchoolId: string
): Promise<{ schoolId: string; cleverSchoolId: string | null }> {
  try {
    return (await callFunction('setSchoolCleverId', { schoolId, cleverSchoolId })) as {
      schoolId: string;
      cleverSchoolId: string | null;
    };
  } catch (error) {
    logger.error('Error mapping school to Clever:', error);
    throw new Error(getErrorMessage(error));
  }
}

export const rosteringService = {
  isCleverEnabled,
  getCleverLoginUrl,
  consumeCleverState,
  completeCleverLogin,
  syncCleverRoster,
  setSchoolCleverId,
};
